import React, { useState } from 'react';
import { BarChart3, Brain, TestTube, Pill, Activity, Calendar, Clock } from 'lucide-react';
import { Patient, Medication, ActionPlan } from '../types/patient';
import PatientHeader from './PatientHeader';
import HealthMetricsOverview from './HealthMetricsOverview';
import WellbeingFactors from './WellbeingFactors';
import LabResults from './LabResults';
import Medications from './Medications';
import AISuggestions from './AISuggestions';
import VitalsCard from './VitalsCard';
import WearableData from './WearableData';
import HealthPlans from './HealthPlans';
import MedicalTimeline from './MedicalTimeline';

interface DashboardProps {
  patient: Patient;
  onBack: () => void;
  onMedicationAdded: (patientId: string, medication: Medication) => void;
  onPlanAdded?: (patientId: string, plan: ActionPlan) => void;
}

export default function Dashboard({ patient, onBack, onMedicationAdded, onPlanAdded }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'wellbeing' | 'labs' | 'medications' | 'Wearable Data' | 'HealthPlans' | 'history'>('overview');

  const tabs = [
    { id: 'overview', name: 'Health Overview', icon: BarChart3 },
    { id: 'wellbeing', name: 'Wellbeing Factors', icon: Brain },
    { id: 'labs', name: 'Lab Results', icon: TestTube },
    { id: 'medications', name: 'Medications', icon: Pill },
    { id: 'Wearable Data', name: 'Wearable Data', icon: Activity },
    { id: 'HealthPlans', name: 'Action Plans', icon: Calendar },
    { id: 'history', name: 'Medical History', icon: Clock },
  ];

  return (
    <div className="space-y-4">
      <PatientHeader patient={patient} onBack={onBack} />

      {/* Navigation Tabs */}
      <div className="border-b border-brand-taupe-300">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" role="tablist" aria-label="Dashboard sections">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-dark-500 text-brand-dark-600'
                    : 'border-transparent text-brand-taupe-600 hover:text-brand-dark-500 hover:border-brand-taupe-400'
                }`}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
              >
                <Icon className="h-4 w-4 mr-2" aria-hidden="true" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* AI Insights (70%) and Vitals (30%) - Side by side on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
              {/* AI Insights - 70% width on desktop */}
              <div className="lg:col-span-7">
                <AISuggestions patient={patient} compact={true} />
              </div>

              {/* Vitals Card - 30% width on desktop */}
              <div className="lg:col-span-3">
                <VitalsCard patient={patient} />
              </div>
            </div>

            {/* Health Metrics Overview - Full Width */}
            <HealthMetricsOverview patient={patient} />
          </div>
        )}
        
        {activeTab === 'wellbeing' && (
          <WellbeingFactors patient={patient} />
        )}
        
        {activeTab === 'labs' && (
          <LabResults patient={patient} />
        )}
        
        {activeTab === 'medications' && (
          <Medications patient={patient} onMedicationAdded={onMedicationAdded} />
        )}
        {activeTab === 'Wearable Data' && (
          <WearableData patient={patient} />
        )}
        {activeTab === 'HealthPlans' && (
          <HealthPlans patient={patient} onPlanAdded={onPlanAdded} />
        )}
        {activeTab === 'history' && (
          <MedicalTimeline patient={patient} />
        )}
      </div>
    </div>
  );
}
